import React from 'react';
import { Box } from '@mui/material';
import { ExtendedMetadataConfigReadExternal } from '@askporter/client-grieg-lyric';
import { DayTimeType } from '../../DayTimeAndRange/DayTime/types';
import { Switch } from '../../Switch';
import { Typography } from '../../Typography';
import { CleaningTaskCard } from './components';
import { CleaningManagementValues } from './types';
import { getAttributeDisplayName } from './utils';

export type { CleaningManagementValues } from './types';

/**
 * The `jsonElementName` of the Extended-Metadata group this bespoke form renders.
 * The generic `ExtendedMetadata` page branches to `CleaningManagementForm` when the
 * selected group matches this key, and renders the generic RJSF form for every other group.
 */
export const CLEANING_MANAGEMENT_JSON_ELEMENT_NAME = 'cleaningManagement';

export interface CleaningManagementFormProps {
  /**
   * Translation function.
   */
  t: (key: string, options?: Record<string, string | number>) => string;
  /**
   * Whether we are on a small-screen device (forwarded to the schedule edit modal).
   */
  isSmallDevice: boolean;
  /**
   * The `cleaningManagement` EMD group config — provides attribute display names.
   */
  config: ExtendedMetadataConfigReadExternal;
  /**
   * The current `cleaningManagement` values, keyed by attribute `jsonElementName`.
   */
  values: CleaningManagementValues;
  /**
   * Called with the full, updated values object whenever the user edits a field.
   */
  onChange: (values: CleaningManagementValues) => void;
  /**
   * Render the whole form read-only (e.g. the user lacks write permission).
   */
  readOnly?: boolean;
}

/**
 * A bespoke, cleaning-specific renderer for the `cleaningManagement` Extended-Metadata
 * group. It is a drop-in alternative to the generic RJSF form: it consumes the same
 * group config + values and emits the same value shape, so the surrounding page keeps
 * ownership of fetching and persistence.
 *
 * It renders the FIRST cleaning task type only — the master switch, then one card built from the
 * fixed `clean*` attribute names.
 *
 * Note what that means for a group holding several cleaning task types: every group named
 * `cleaningManagement` gets this form, and only the first task type's fields are editable in it.
 * Any others stay in the saved values but have no editor here. In practice the tenants with more
 * than one task type name their group differently, so they keep the generic form — but that is
 * their naming, not a rule this component enforces. Splitting a group by task type needs the
 * config to say which attribute belongs to which, which the API cannot express yet.
 */
export const CleaningManagementForm: React.FC<CleaningManagementFormProps> = ({
  t,
  isSmallDevice,
  config,
  values,
  onChange,
  readOnly = false,
}: CleaningManagementFormProps) => {
  const attributes = config?.attributes;

  const update = (patch: Partial<CleaningManagementValues>): void => onChange({ ...values, ...patch });

  // The attribute names of the first cleaning task type. Any further task types in the same group
  // name their fields differently per tenant, so there is nothing reliable to read them by.
  // Every label the form renders is the display name of the field it labels, which the API has
  // already resolved into the caller's language. Nothing is invented here: a field the config
  // does not name simply has no label, rather than one this component made up.
  const taskTitle = getAttributeDisplayName(attributes, 'cleanSettingsGroup1Label');
  const enabledLabel = getAttributeDisplayName(attributes, 'cleanRequired');
  const triggerLabel = getAttributeDisplayName(attributes, 'cleanTrigger');
  const statusLabel = getAttributeDisplayName(attributes, 'cleanStatus');
  const lastUpdatedLabel = getAttributeDisplayName(attributes, 'lastUpdated');
  const scheduleLabel = getAttributeDisplayName(attributes, 'cleanSchedule');
  const historyLabel = getAttributeDisplayName(attributes, 'cleanHistory');
  const globalEnabledLabel = getAttributeDisplayName(attributes, 'checkingEnabled');

  const globalSwitch = (
    <Switch
      checked={!!values.checkingEnabled}
      disabled={readOnly}
      onChange={(e) => update({ checkingEnabled: e.target.checked })}
      slotProps={{ input: { 'aria-label': globalEnabledLabel } }}
    />
  );

  return (
    <Box
      data-testid="cleaning-management-form"
      sx={{ display: 'flex', flexDirection: 'column', gap: '8px', maxWidth: '720px' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '8px' }}>
        {globalEnabledLabel && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', ml: 'auto' }}>
            <Typography sx={{ fontSize: '13px', color: '#475467' }}>{globalEnabledLabel}</Typography>
            {globalSwitch}
          </Box>
        )}
      </Box>

      <CleaningTaskCard
        t={t}
        isSmallDevice={isSmallDevice}
        title={taskTitle}
        status={values.cleanStatus}
        lastUpdated={values.lastUpdated}
        history={values.cleanHistory}
        schedule={values.cleanSchedule}
        enabled={values.cleanRequired}
        enabledLabel={enabledLabel}
        statusLabel={statusLabel}
        lastUpdatedLabel={lastUpdatedLabel}
        scheduleLabel={scheduleLabel}
        historyLabel={historyLabel}
        trigger={values.cleanTrigger}
        triggerLabel={triggerLabel}
        readOnly={readOnly}
        onScheduleChange={(schedule: DayTimeType[]) => update({ cleanSchedule: schedule })}
        onTriggerChange={(trigger: number) => update({ cleanTrigger: trigger })}
        onEnabledChange={(enabledValue: boolean) => update({ cleanRequired: enabledValue })}
      />
    </Box>
  );
};
